import { useEffect, useState } from 'react';
import {
  Badge,
  Box,
  Button,
  Heading,
  HStack,
  Icon,
  Input,
  InputGroup,
  Spinner,
  Stack,
  Table,
  Tabs,
  Text,
} from '@chakra-ui/react';
import { Link as RouterLink, useNavigate } from 'react-router-dom';
import { LuPencil, LuPlus, LuSearch } from 'react-icons/lu';
import { listSellers, type SellerListRow, type SellerStatus } from '@/lib/api';

function DealCountChip({ count }: { count: number }) {
  if (count === 0) return <Badge colorPalette="gray" size="sm">Sin ventas</Badge>;
  return <Badge colorPalette="blue" size="sm">{count === 1 ? '1 venta' : `${count} ventas`}</Badge>;
}

export function Sellers() {
  const navigate = useNavigate();
  const [tab, setTab] = useState<SellerStatus>('active');
  const [q, setQ] = useState('');
  const [debouncedQ, setDebouncedQ] = useState('');
  const [rows, setRows] = useState<SellerListRow[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    const t = setTimeout(() => setDebouncedQ(q.trim()), 250);
    return () => clearTimeout(t);
  }, [q]);

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    setError(null);
    listSellers({ status: tab, q: debouncedQ || undefined })
      .then((data) => {
        if (!cancelled) setRows(data);
      })
      .catch((err) => {
        if (!cancelled) {
          setError(err instanceof Error ? err.message : 'No se pudo cargar la lista');
          setRows([]);
        }
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [tab, debouncedQ]);

  return (
    <Box px={{ base: '4', md: '8' }} py={{ base: '6', md: '8' }}>
      <Stack gap="6" maxW="6xl">
        <Stack
          direction={{ base: 'column', md: 'row' }}
          justify="space-between"
          align={{ base: 'stretch', md: 'flex-end' }}
          gap="4"
        >
          <Stack gap="1">
            <Heading size={{ base: 'xl', md: '2xl' }} letterSpacing="-0.02em">
              Vendedores
            </Heading>
            <Text color="fg.muted" fontSize={{ base: 'sm', md: 'md' }}>
              Equipo comercial. Se asignan a contratos y a clientes como responsable de cuenta.
            </Text>
          </Stack>
          <Button
            colorPalette="brand"
            bg="brand.600"
            color="white"
            _hover={{ bg: 'brand.700' }}
            onClick={() => navigate('/vendedores/nuevo')}
            alignSelf={{ base: 'stretch', md: 'flex-end' }}
          >
            <Icon as={LuPlus} /> Nuevo vendedor
          </Button>
        </Stack>

        <Tabs.Root value={tab} onValueChange={(v) => setTab(v.value as SellerStatus)}>
          <Stack
            direction={{ base: 'column', md: 'row' }}
            gap="3"
            align={{ base: 'stretch', md: 'center' }}
            justify="space-between"
          >
            <Tabs.List>
              <Tabs.Trigger value="active">Activos</Tabs.Trigger>
              <Tabs.Trigger value="archived">Archivados</Tabs.Trigger>
            </Tabs.List>
            <InputGroup startElement={<Icon as={LuSearch} color="fg.muted" />} w={{ base: 'full', md: '320px' }}>
              <Input value={q} onChange={(e) => setQ(e.currentTarget.value)} placeholder="Buscar por nombre o correo…" />
            </InputGroup>
          </Stack>

          <Box pt="6">
            {error && (
              <Text color="red.600" fontSize="sm" mb="4">
                {error}
              </Text>
            )}
            {loading ? (
              <HStack gap="3" color="fg.muted">
                <Spinner size="sm" />
                <Text fontSize="sm">Cargando…</Text>
              </HStack>
            ) : rows.length === 0 ? (
              <Box bg="bg.surface" borderWidth="1px" borderColor="border" rounded="lg" p="10" textAlign="center">
                <Text color="fg.muted" fontSize="sm">
                  {debouncedQ
                    ? `No hay vendedores que coincidan con "${debouncedQ}".`
                    : tab === 'active'
                      ? 'Aún no hay vendedores. Crea el primero para empezar.'
                      : 'No hay vendedores archivados.'}
                </Text>
              </Box>
            ) : (
              <>
                {/* Mobile: card list */}
                <Stack gap="3" display={{ base: 'flex', md: 'none' }}>
                  {rows.map((s) => (
                    <Box key={s.id} bg="bg.surface" borderWidth="1px" borderColor="border" rounded="lg" p="4">
                      <Stack gap="3">
                        <HStack justify="space-between" align="flex-start" gap="3">
                          <Stack gap="0.5" minW={0}>
                            <Text fontWeight="600" lineHeight="1.2">
                              {s.fullName}
                            </Text>
                            {(s.email || s.phone) && (
                              <Text fontSize="sm" color="fg.muted" truncate>
                                {[s.phone, s.email].filter(Boolean).join(' · ')}
                              </Text>
                            )}
                          </Stack>
                          <Badge colorPalette={s.status === 'active' ? 'green' : 'gray'} flexShrink={0}>
                            {s.status === 'active' ? 'Activo' : 'Archivado'}
                          </Badge>
                        </HStack>
                        <HStack gap="2" justify="space-between" wrap="wrap">
                          <DealCountChip count={s.dealCount} />
                          <Button size="xs" variant="outline" asChild>
                            <RouterLink to={`/vendedores/${s.id}`}>
                              <Icon as={LuPencil} /> Editar
                            </RouterLink>
                          </Button>
                        </HStack>
                      </Stack>
                    </Box>
                  ))}
                </Stack>

                {/* Desktop: table */}
                <Box
                  bg="bg.surface"
                  borderWidth="1px"
                  borderColor="border"
                  rounded="lg"
                  overflow="hidden"
                  display={{ base: 'none', md: 'block' }}
                >
                  <Table.Root size="sm">
                    <Table.Header>
                      <Table.Row>
                        <Table.ColumnHeader>Nombre</Table.ColumnHeader>
                        <Table.ColumnHeader>Contacto</Table.ColumnHeader>
                        <Table.ColumnHeader>Ventas</Table.ColumnHeader>
                        <Table.ColumnHeader>Estado</Table.ColumnHeader>
                        <Table.ColumnHeader textAlign="right">Acciones</Table.ColumnHeader>
                      </Table.Row>
                    </Table.Header>
                    <Table.Body>
                      {rows.map((s) => (
                        <Table.Row key={s.id}>
                          <Table.Cell fontWeight="600">{s.fullName}</Table.Cell>
                          <Table.Cell color="fg.muted">
                            {[s.phone, s.email].filter(Boolean).join(' · ') || '—'}
                          </Table.Cell>
                          <Table.Cell>
                            <DealCountChip count={s.dealCount} />
                          </Table.Cell>
                          <Table.Cell>
                            <Badge colorPalette={s.status === 'active' ? 'green' : 'gray'}>
                              {s.status === 'active' ? 'Activo' : 'Archivado'}
                            </Badge>
                          </Table.Cell>
                          <Table.Cell>
                            <HStack justify="flex-end" gap="2">
                              <Button size="xs" variant="outline" asChild>
                                <RouterLink to={`/vendedores/${s.id}`}>
                                  <Icon as={LuPencil} /> Editar
                                </RouterLink>
                              </Button>
                            </HStack>
                          </Table.Cell>
                        </Table.Row>
                      ))}
                    </Table.Body>
                  </Table.Root>
                </Box>
              </>
            )}
          </Box>
        </Tabs.Root>
      </Stack>
    </Box>
  );
}
